import React, { useCallback, useEffect, useState } from 'react';
import { IDialogProps } from '../../../lib/dialog/Dialog';
import { EditorService } from '../../../services/editor';
import {
	EntityId,
	EntityType,
	PropertyState,
} from '@elyx-code/project-logic-tree';
import { useParams } from 'react-router-dom';
import Property from './Property';
import DialogLoader from '../../DialogLoader';
import { popupNotification } from '../../../popup-notification';
import EntityDialogHeader from '../../EntityDialogHeader';

export interface IPropertyDialogContentProps extends IDialogProps {
	project: EditorService;
}

export interface IPropertySectionProps {
	entity: PropertyState;
	project: EditorService;
}

const PropertyDialogContent: React.FC<IPropertyDialogContentProps> = ({
	onClose,
	project,
}) => {
	const { entityId } = useParams<{
		entityType: EntityType;
		entityId: EntityId;
	}>();

	const entity: PropertyState | null =
		(project?.logic?.get(entityId) as PropertyState | null) || null;

	const onEscapePressed = useCallback(
		(e: KeyboardEvent) => {
			// Or close the dialog if there is only one tab
			onClose(e);
		},
		[onClose]
	);

	const useForceUpdate = () => {
		const [_, setValue] = useState(0);
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	useEffect(() => {
		project?.on('escape-key-press', onEscapePressed);

		if (!!entity) {
			project?.on(entity.id, () => {
				forceUpdate();
			});
		}

		return () => {
			project?.off('escape-key-press', onEscapePressed);
		};
	}, [project, entity, onEscapePressed]);

	if (!project || !entity) {
		if (!!project && project.hasLoadedLogic && !entity) {
			popupNotification({
				color: 'error',
				text: 'This property does not exist in the current version of the project',
			});
			onClose(null);
			return;
		}

		return (
			<DialogLoader
				title='Property'
				subtitle='Property builder'
				onClose={onClose}
			/>
		);
	}

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				overflow: 'hidden',
				paddingBottom: 20,
			}}
		>
			<EntityDialogHeader entity={entity} project={project} onClose={onClose} />

			<Property
				entity={entity}
				project={project}
				onClose={(e) => {
					onClose(e);
				}}
			/>
		</div>
	);
};

export default PropertyDialogContent;
